// ui/src/services/apiClient.js
import { ApiError } from '../utils/errors';
// import { validateApiName } from '../utils/validation'; // Keep if needed for API form validation
import { config } from '../config';
import { getAuthHeaders } from '../utils/getAuthHeaders';
import { logger } from '../utils/logger';

const API_URL = config.apiUrl;
const PROMPT_ENDPOINT = '/prompt';
const CHAT_ENDPOINT = '/chat'; // New GPT-5 endpoint
const COLLECTIONS_ENDPOINT = '/collections';
const TENANTS_ENDPOINT = '/tenants'; // Added for agent routes
const AGENTS_ENDPOINT = '/agents'; // Added for agent registration

// Feature flag for GPT-5 endpoints
const USE_GPT5_ENDPOINTS = process.env.REACT_APP_USE_GPT5_ENDPOINTS === 'true';

// Default GPT-5 parameters
const DEFAULT_GPT5_REASONING_EFFORT = 'medium';
const DEFAULT_GPT5_VERBOSITY = 'medium';

/**
 * Get available GPT-5 parameter options
 * @returns {Object} - Available parameter values for GPT-5
 */
export const getGPT5ParameterOptions = () => ({
  reasoningEffort: ['low', 'medium', 'high'],
  verbosity: ['low', 'medium', 'high'],
  defaults: {
    reasoningEffort: DEFAULT_GPT5_REASONING_EFFORT,
    verbosity: DEFAULT_GPT5_VERBOSITY
  }
});

/**
 * Check if GPT-5 endpoints are enabled
 * @returns {boolean} - Whether GPT-5 endpoints are enabled
 */
export const isGPT5Enabled = () => USE_GPT5_ENDPOINTS;

// Remove global gpt_messages state - manage history in ChatPage component
// const system_prompt = "Today's date is " + calculate_current_date() + "."
// const gpt_messages = [ ... ];

// Keep date function if needed elsewhere, otherwise remove
// function calculate_current_date() { ... }

// Renamed and updated function
export const getDataSourcesForCollection = async (tenant_id, collection_id, token) =>  {
  try {
    if (!tenant_id || !collection_id) {
      logger.error("Tenant ID and Collection ID are required");
      return []; // Return empty array on error or missing IDs
    }
    // Updated endpoint path
    const url = new URL(`${API_URL}${COLLECTIONS_ENDPOINT}/${tenant_id}/${collection_id}/datasources`);
    logger.debug(`Fetching data sources from: ${url.toString()}`);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: getAuthHeaders(token)
    });

    if (!response.ok) {
      // Try to parse error details
      let errorDetail = 'Network response was not ok';
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { /* Ignore parsing error */ }
      logger.error(`Failed to fetch data sources: ${errorDetail}`, response.status);
      throw new ApiError(`Failed to fetch data sources: ${errorDetail}`, response.status);
    }

    const data = await response.json();
    // Log the raw data from the server for debugging
    logger.debug('Data sources fetched from API:', data);
    
    return Array.isArray(data) ? data : []; // Ensure array return

  } catch (error) {
    logger.error('Error fetching data sources:', error);
    // Re-throw specific error type if possible, otherwise return empty
    if (error instanceof ApiError) throw error;
    throw new ApiError('Failed to fetch data sources', 500, error.message);
    // return []; // Or return empty array
  }
}

/**
 * Execute a single API tool call from the LLM
 * @param {Object} toolCall - The tool call object from the LLM
 * @param {Array} dataSources - Collection of data sources to find the matching API
 * @param {Object} collectionHeaders - Headers from the collection to include in API requests
 * @returns {Promise<Object>} - The result of the API call
 */
export const executeToolCall = async (toolCall, dataSources, collectionHeaders = {}) => {
  try {
    logger.debug('Executing tool call:', toolCall);
    
    if (!toolCall || !toolCall.function) {
      throw new Error('Invalid tool call object');
    }
    
    const functionName = toolCall.function.name;
    let args = {};
    
    try {
      args = typeof toolCall.function.arguments === 'string' 
        ? JSON.parse(toolCall.function.arguments) 
        : toolCall.function.arguments;
    } catch (e) {
      logger.error('Failed to parse tool call arguments:', e);
      throw new Error(`Invalid arguments in tool call: ${e.message}`);
    }
    
    logger.debug('Looking for API named:', functionName);
    logger.debug('Available data sources:', dataSources);
    
    // Special handling for execute_sql_query - this is a database query tool, not an API
    if (functionName === 'execute_sql_query') {
      logger.debug('Handling SQL query execution - returning success response');
      return {
        tool_call_id: toolCall.id,
        name: functionName,
        content: {
          success: true,
          message: "SQL query is being evaluated..."
        }
      };
    }
    
    // Find the matching API definition from the data sources
    // Use case-insensitive comparison to be more forgiving
    const apiDataSource = dataSources.find(ds => {
      // Check if ds is valid
      if (!ds) return false;
      
      // Support both snake_case and camelCase/PascalCase properties
      const dsType = ds.data_source_type || ds.DataSourceType;
      const dsName = ds.name || ds.Name;
      const isEnabled = ds.is_enabled !== false && ds.IsEnabled !== false;
      
      // Make case-insensitive comparison and ensure it's an API type
      return dsType === 'API' && 
             isEnabled && 
             dsName && 
             dsName.toLowerCase() === functionName.toLowerCase();
    });
    
    if (!apiDataSource) {
      logger.error(`API "${functionName}" not found in available data sources`, dataSources);
      throw new Error(`API "${functionName}" not found in available data sources`);
    }
    
    // Extract API details supporting both property naming conventions
    const endpoint = apiDataSource.endpoint || apiDataSource.Endpoint;
    const method = apiDataSource.http_method || apiDataSource.HttpMethod;
    const apiAuthHeaders = apiDataSource.auth_headers || apiDataSource.AuthHeaders;
    
    logger.debug(`Found API definition for ${functionName}:`, apiDataSource);
    
    // Build the request
    let url = endpoint;
    let body = null;
    let params = null;
    
    // Handle different HTTP methods
    if (method.toUpperCase() === 'GET') {
      // For GET, replace URL params or use query params
      if (url.includes('{')) {
        // Replace URL parameters with actual values
        Object.entries(args).forEach(([key, value]) => {
          url = url.replace(`{${key}}`, encodeURIComponent(String(value)));
        });
      } else {
        // Use query parameters
        const queryParams = new URLSearchParams();
        Object.entries(args).forEach(([key, value]) => {
          queryParams.append(key, String(value));
        });
        url = `${url}${url.includes('?') ? '&' : '?'}${queryParams.toString()}`;
      }
    } else {
      // For POST, PUT, etc., use body
      body = JSON.stringify(args);
    }
    
    // Build headers with collection headers and API-specific auth headers
    // API-level headers take precedence over collection headers
    const headers = {
      'Content-Type': 'application/json',
      ...collectionHeaders,  // Collection-level headers first
      ...apiAuthHeaders      // API-level headers override
    };
    
    // Debug logging for development
    if (process.env.NODE_ENV === 'development') {
      logger.debug('Collection headers:', collectionHeaders);
      logger.debug('API headers:', apiAuthHeaders);
      logger.debug('Final merged headers:', headers);
    }
    
    logger.debug(`Making ${method} request to ${url}`);
    
    // Make the request
    const response = await fetch(url, {
      method: method.toUpperCase(),
      headers,
      body: method.toUpperCase() !== 'GET' ? body : undefined
    });
    
    let result;
    try {
      // Try to parse as JSON first
      result = await response.json();
    } catch (e) {
      // If not JSON, get text
      result = await response.text();
    }
    
    if (!response.ok) {
      logger.error(`API call failed (${response.status}):`, result);
      throw new Error(`API call failed with status ${response.status}: ${JSON.stringify(result)}`);
    }
    
    logger.debug(`API call succeeded:`, result);
    
    return {
      tool_call_id: toolCall.id,
      name: functionName,
      content: result
    };
  } catch (error) {
    logger.error('Error executing tool call:', error);
    // Return error as the result so the LLM can handle/explain it
    return {
      tool_call_id: toolCall.id,
      name: toolCall.function?.name || 'unknown',
      content: {
        error: true,
        message: error.message || 'An error occurred during API execution'
      }
    };
  }
};

/**
 * Process multiple tool calls from the LLM
 * @param {Array} toolCalls - Array of tool calls from the LLM
 * @param {Array} dataSources - Collection data sources
 * @param {Object} collectionHeaders - Headers from the collection to include in API requests
 * @returns {Promise<Array>} - Results from all tool calls
 */
export const executeToolCalls = async (toolCalls, dataSources, collectionHeaders = {}) => {
  if (!toolCalls || !Array.isArray(toolCalls) || toolCalls.length === 0) {
    logger.debug('No tool calls to execute');
    return [];
  }
  
  logger.debug(`Executing ${toolCalls.length} tool calls...`);
  
  // Execute all tool calls in parallel
  const results = await Promise.all(
    toolCalls.map(toolCall => executeToolCall(toolCall, dataSources, collectionHeaders))
  );
  
  logger.debug('All tool calls completed:', results);
  return results;
};

/**
 * Send results of tool call execution back to the backend for the next LLM response (GPT-5)
 * @param {Array} messages - Conversation history
 * @param {Array} functionResults - Results from executed tool calls
 * @param {string} tenantId - Tenant ID
 * @param {string} collectionId - Collection ID
 * @param {string} token - Auth token
 * @param {string} reasoningEffort - GPT-5 reasoning effort ('low', 'medium', 'high')
 * @param {string} verbosity - GPT-5 verbosity ('low', 'medium', 'high')
 * @returns {Promise<Object>} - Next LLM response
 */
export const sendFunctionResultsToAPIWithGPT5 = async (
  messages,
  functionResults,
  tenantId,
  collectionId,
  token,
  reasoningEffort = DEFAULT_GPT5_REASONING_EFFORT,
  verbosity = DEFAULT_GPT5_VERBOSITY
) => {
  try {
    // The backend endpoint for GPT-5 function results
    const url = `${API_URL}${CHAT_ENDPOINT}/${collectionId}/tool-call`;
    logger.debug(`Sending function results to GPT-5 endpoint: ${url}`);
    logger.debug(`GPT-5 parameters - reasoning_effort: ${reasoningEffort}, verbosity: ${verbosity}`);
    
    // Ensure properly formatted messages with tool responses
    // Find the most recent assistant message with tool calls
    let lastAssistantMessageWithToolsIndex = -1;
    
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && 
          messages[i].tool_calls && 
          messages[i].tool_calls.length > 0) {
        lastAssistantMessageWithToolsIndex = i;
        break;
      }
    }
    
    if (lastAssistantMessageWithToolsIndex === -1) {
      throw new Error("No assistant message with tool calls found in conversation history");
    }
    
    // Use messages up to and including the assistant's tool call message
    const formattedMessages = messages.slice(0, lastAssistantMessageWithToolsIndex + 1);
    
    logger.debug("Messages sent to GPT-5 (without tool results appended):", formattedMessages);
    
    const requestBody = {
      messages: formattedMessages,
      function_results: functionResults,
      tenant_id: tenantId,
      reasoning_effort: reasoningEffort,
      verbosity: verbosity
    };
    
    logger.debug("GPT-5 request body:", requestBody);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      let errorDetails = 'Network response was not ok';
      try {
        errorDetails = await response.json();
      } catch(e) { /* ignore json parsing error */ }
      logger.error("Detailed error sending function results to GPT-5:", errorDetails);
      throw new ApiError(errorDetails.detail || 'Network response was not ok', response.status);
    }
    
    const data = await response.json();
    logger.debug("GPT-5 API response after function execution:", data);
    
    return data;
  } catch (error) {
    logger.error('Error sending function results to GPT-5 API:', error);
    if (error instanceof ApiError) throw error;
    throw new ApiError('Failed to process function results with GPT-5', 500, error.message);
  }
};

/**
 * Send results of tool call execution back to the backend for the next LLM response
 * @param {Array} messages - Conversation history
 * @param {Array} functionResults - Results from executed tool calls
 * @param {string} tenantId - Tenant ID
 * @param {string} collectionId - Collection ID
 * @param {string} token - Auth token
 * @param {string} reasoningEffort - GPT-5 reasoning effort ('low', 'medium', 'high')
 * @param {string} verbosity - GPT-5 verbosity ('low', 'medium', 'high')
 * @returns {Promise<Object>} - Next LLM response
 */
export const sendFunctionResultsToAPI = async (
  messages,
  functionResults,
  tenantId,
  collectionId,
  token,
  reasoningEffort = DEFAULT_GPT5_REASONING_EFFORT,
  verbosity = DEFAULT_GPT5_VERBOSITY
) => {
  if (USE_GPT5_ENDPOINTS) {
    logger.debug('Using GPT-5 endpoint for function results');
    return sendFunctionResultsToAPIWithGPT5(
      messages,
      functionResults,
      tenantId,
      collectionId,
      token,
      reasoningEffort,
      verbosity
    );
  }
  try {
    // The backend endpoint for function results
    const url = `${API_URL}${PROMPT_ENDPOINT}/${collectionId}/function_result`;
    logger.debug(`Sending function results to: ${url}`);
    
    // Ensure properly formatted messages with tool responses
    // Find the most recent assistant message with tool calls
    let lastAssistantMessageWithToolsIndex = -1;
    
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && 
          messages[i].tool_calls && 
          messages[i].tool_calls.length > 0) {
        lastAssistantMessageWithToolsIndex = i;
        break;
      }
    }
    
    if (lastAssistantMessageWithToolsIndex === -1) {
      throw new Error("No assistant message with tool calls found in conversation history");
    }
    
    // Use messages up to and including the assistant's tool call message
    const formattedMessages = messages.slice(0, lastAssistantMessageWithToolsIndex + 1);
    
    // The backend (GptService.handle_tool_call) is now responsible for appending
    // the tool results to the message history before calling OpenAI.
    // We only send the history up to the assistant's request.
    
    logger.debug("Messages sent (without tool results appended):", formattedMessages);
    
    const requestBody = {
      messages: formattedMessages,
      function_results: functionResults,
      tenant_id: tenantId
    };
    
    logger.debug("Request body:", requestBody);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      let errorDetails = 'Network response was not ok';
      try {
        errorDetails = await response.json();
      } catch(e) { /* ignore json parsing error */ }
      logger.error("Detailed error sending function results:", errorDetails);
      throw new ApiError(errorDetails.detail || 'Network response was not ok', response.status);
    }
    
    const data = await response.json();
    logger.debug("API response after function execution:", data);
    
    return data;
  } catch (error) {
    logger.error('Error sending function results to API:', error);
    if (error instanceof ApiError) throw error;
    throw new ApiError('Failed to process function results', 500, error.message);
  }
};

/**
 * Send message to GPT-5 chat endpoint
 * @param {Array} messages - Conversation history
 * @param {string} tenantId - Tenant ID
 * @param {string} collectionId - Collection ID
 * @param {string} token - Auth token
 * @param {string} reasoningEffort - GPT-5 reasoning effort ('low', 'medium', 'high')
 * @param {string} verbosity - GPT-5 verbosity ('low', 'medium', 'high')
 * @returns {Promise<Object>} - LLM response
 */
export const sendMessageToAPIWithGPT5 = async (
    messages,
    tenantId,
    collectionId,
    token,
    reasoningEffort = DEFAULT_GPT5_REASONING_EFFORT,
    verbosity = DEFAULT_GPT5_VERBOSITY
) => {
  try {
      logger.debug("Sending messages to GPT-5 API:", messages);
      logger.debug(`GPT-5 parameters - reasoning_effort: ${reasoningEffort}, verbosity: ${verbosity}`);
      
      const url = `${API_URL}${CHAT_ENDPOINT}/${collectionId}`;
      logger.debug(`Sending message to GPT-5 endpoint: ${url}`);
      
      const requestBody = {
          messages: messages,
          tenant_id: tenantId,
          collection_id: collectionId,
          reasoning_effort: reasoningEffort,
          verbosity: verbosity
      };
      
      logger.debug("GPT-5 request body:", requestBody);
      
      const response = await fetch(url, {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
          let errorDetails = 'Network response was not ok';
          try {
              errorDetails = await response.json();
          } catch(e) { /* ignore json parsing error */ }
          logger.error("Detailed error sending message to GPT-5: ", errorDetails);
          throw new ApiError(errorDetails.detail || 'Network response was not ok', response.status);
      }

      const data = await response.json();
      logger.debug("GPT-5 API response data:", data);

      return data;

  } catch (error) {
      logger.error('Error sending message to GPT-5 API:', error);
      if (error instanceof ApiError) throw error;
      throw new ApiError('Failed to send message to GPT-5', 500, error.message);
  }
};

// Updated function signature and logic
export const sendMessageToAPI = async (
    messages, // Expecting full message history [{role: 'user'/'assistant', content: '...', tool_calls?: [...]}]
    tenantId,
    collectionId,
    token,
    reasoningEffort = DEFAULT_GPT5_REASONING_EFFORT,
    verbosity = DEFAULT_GPT5_VERBOSITY
) => {
  if (USE_GPT5_ENDPOINTS) {
    logger.debug('Using GPT-5 endpoint for message sending');
    return sendMessageToAPIWithGPT5(
      messages,
      tenantId,
      collectionId,
      token,
      reasoningEffort,
      verbosity
    );
  }
  try {
      // Log the messages being sent for debugging tool calls
      logger.debug("Sending messages to API:", messages);
      
      // The backend now manages the conversation state and tool definitions
      // Construct the correct endpoint URL including collectionId
      const url = `${API_URL}${PROMPT_ENDPOINT}/${collectionId}`;
      logger.debug(`Sending message to: ${url}`);
      
      const requestBody = {
          messages: messages,
          tenant_id: tenantId,
          collection_id: collectionId
      };
      
      logger.debug("Request body:", requestBody);
      
      const response = await fetch(url, { // Use the constructed URL
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
          let errorDetails = 'Network response was not ok';
          try {
              errorDetails = await response.json();
          } catch(e) { /* ignore json parsing error */ }
          logger.error("Detailed error sending message: ", errorDetails);
          throw new ApiError(errorDetails.detail || 'Network response was not ok', response.status);
      }

      const data = await response.json();
      logger.debug("API response data:", data);

      // The backend response should contain the updated message list and the latest assistant choice
      // The frontend (ChatPage) will use this response to update its state.
      return data; // Return the full response from the backend

  } catch (error) {
      logger.error('Error sending message to API:', error);
      if (error instanceof ApiError) throw error;
      throw new ApiError('Failed to send message', 500, error.message);
  }
};

// --- Collection CRUD (Keep existing, ensure they match backend if changed) ---

export const getTenantCollections = async (tenant_id, token) => {
  try {
    const response = await fetch(`${API_URL}${COLLECTIONS_ENDPOINT}/${tenant_id}`, { 
      headers: getAuthHeaders(token)
    });
    if (!response.ok) {
        let errorDetails = 'Failed to fetch collections';
        try { errorDetails = (await response.json()).detail || errorDetails } catch(e){}
        throw new ApiError(errorDetails, response.status);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logger.error('Error fetching tenant collections:', error);
    if (error instanceof ApiError) throw error;
    throw new ApiError('Failed to fetch collections', 500, error.message);
  }
};

// Get specific collection details (might include datasources now)
export const getCollectionDetails = async (tenant_id, collection_id, token) => {
  try {
    const response = await fetch(`${API_URL}${COLLECTIONS_ENDPOINT}/${tenant_id}/${collection_id}`, { 
      headers: getAuthHeaders(token)
    });
     if (!response.ok) {
        let errorDetails = 'Failed to fetch collection details';
        try { errorDetails = (await response.json()).detail || errorDetails } catch(e){}
        throw new ApiError(errorDetails, response.status);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    logger.error('Error fetching collection details:', error);
     if (error instanceof ApiError) throw error;
    throw new ApiError('Failed to fetch collection details', 500, error.message);
  }
};


export const createCollection = async (tenantId, collection, token) => {
  try {
    if (!tenantId) throw new ApiError('Tenant ID is required', 400);
    if (!collection.name?.trim()) throw new ApiError('Collection name is required', 400);

    // Backend expects snake_case
    const payload = {
        tenant_id: tenantId,
        collection_name: collection.name.trim(),
        collection_description: collection.description?.trim() || null, // Send null if empty
        auth_headers: collection.authHeaders || null, // Send null if empty/undefined
        data_sources: [] // Data sources added separately
    };

    const response = await fetch(`${API_URL}/collections`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(data.detail || 'Failed to create collection', response.status, data.errors);
    }
    return data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Failed to create collection', 500, error.message);
  }
};

export const updateCollection = async (tenantId, collectionId, collection, token) => {
  try {
    if (!tenantId) throw new ApiError('Tenant ID is required', 400);
    if (!collectionId) throw new ApiError('Collection ID is required', 400);
    if (!collection.name?.trim()) throw new ApiError('Collection name is required', 400);

    // Backend expects snake_case
    const payload = {
      collection_name: collection.name.trim(),
      collection_description: collection.description?.trim() || null, // Send null if empty
      auth_headers: collection.authHeaders || null // Send null if empty/undefined
    };

    const response = await fetch(`${API_URL}${COLLECTIONS_ENDPOINT}/${tenantId}/${collectionId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(data.detail || 'Failed to update collection', response.status, data.errors);
    }
    return data;
  } catch (error) {
    logger.error('Error updating collection:', error);
    if (error instanceof ApiError) throw error;
    throw new ApiError('Failed to update collection', 500, error.message);
  }
};

// REMOVED: createApi, updateApi, deleteApi - Handled via DataSource endpoints now

export const getCollections = async (tenantId, token) => {
  try {
    const response = await fetch(`${API_URL}/collections/${tenantId}`, { 
      headers: getAuthHeaders(token)
    });
    if (!response.ok) {
      throw new ApiError('Failed to fetch collections', response.status);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Failed to fetch collections', 500, error.message);
  }
};

// --- DataSource CRUD Functions (NEW - Placeholders) ---

export const addDataSource = async (tenantId, collectionId, dataSourceData, token) => {
    logger.debug("API Client: Adding DataSource", { tenantId, collectionId, dataSourceData });
    // TODO: Implement actual API call to POST /collections/{tenantId}/{collectionId}/datasources
    // Determine if API or DB type and send appropriate payload
    const endpoint = `${API_URL}${COLLECTIONS_ENDPOINT}/${tenantId}/${collectionId}/datasources`;
    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: getAuthHeaders(token),
            body: JSON.stringify(dataSourceData) // Assumes dataSourceData is already formatted correctly
        });
        const data = await response.json();
        if (!response.ok) {
            throw new ApiError(data.detail || 'Failed to add data source', response.status);
        }
        return data; // Return the created data source
    } catch (error) {
         logger.error('Error adding data source:', error);
         if (error instanceof ApiError) throw error;
         throw new ApiError('Failed to add data source', 500, error.message);
    }
};

export const updateDataSource = async (tenantId, collectionId, dataSourceId, dataSourceData, token) => {
    logger.debug("API Client: Updating DataSource", { tenantId, collectionId, dataSourceId, dataSourceData });
    // TODO: Implement actual API call to PUT /collections/{tenantId}/{collectionId}/datasources/{dataSourceId}
     const encodedDataSourceId = encodeURIComponent(dataSourceId); // Explicitly encode the ID
     const endpoint = `${API_URL}${COLLECTIONS_ENDPOINT}/${tenantId}/${collectionId}/datasources/${encodedDataSourceId}`;
     try {
         const response = await fetch(endpoint, {
             method: 'PUT',
             headers: getAuthHeaders(token),
             body: JSON.stringify(dataSourceData) // Assumes dataSourceData is the full updated object
         });
         const data = await response.json();
         if (!response.ok) {
             throw new ApiError(data.detail || 'Failed to update data source', response.status);
         }
         return data; // Return the updated data source
     } catch (error) {
          logger.error('Error updating data source:', error);
          if (error instanceof ApiError) throw error;
          throw new ApiError('Failed to update data source', 500, error.message);
     }
};

export const deleteDataSource = async (tenantId, collectionId, dataSourceId, token) => {
    logger.debug("API Client: Deleting DataSource", { tenantId, collectionId, dataSourceId });
    // TODO: Implement actual API call to DELETE /collections/{tenantId}/{collectionId}/datasources/{dataSourceId}
    const encodedDataSourceId = encodeURIComponent(dataSourceId); // Explicitly encode the ID
    const endpoint = `${API_URL}${COLLECTIONS_ENDPOINT}/${tenantId}/${collectionId}/datasources/${encodedDataSourceId}`;
     try {
         const response = await fetch(endpoint, {
             method: 'DELETE',
             headers: getAuthHeaders(token),
         });
         // DELETE often returns 204 No Content on success
         if (!response.ok && response.status !== 204) {
             let errorDetails = 'Failed to delete data source';
             try { errorDetails = (await response.json()).detail || errorDetails } catch(e){}
             throw new ApiError(errorDetails, response.status);
         }
         return true; // Indicate success
     } catch (error) {
          logger.error('Error deleting data source:', error);
          if (error instanceof ApiError) throw error;
          throw new ApiError('Failed to delete data source', 500, error.message);
     }
};


// --- Agent CRUD Functions (NEW) ---

export const getAgentsForTenant = async (tenant_id, headers) => { // Expect headers object
  try {
    if (!tenant_id) {
      logger.error("Tenant ID is required");
      return []; // Return empty array on error or missing ID
    }
    
    const url = new URL(`${API_URL}${TENANTS_ENDPOINT}/${tenant_id}/agents`);
    logger.debug(`Fetching agents from: ${url.toString()}`);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: headers // Use passed headers directly
    });

    if (!response.ok) {
      // Try to parse error details
      let errorDetail = 'Network response was not ok';
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { /* Ignore parsing error */ }
      logger.error(`Failed to fetch agents: ${errorDetail}`, response.status);
      throw new ApiError(`Failed to fetch agents: ${errorDetail}`, response.status);
    }

    const data = await response.json();
    // Log the raw data from the server for debugging
    logger.debug('Agents fetched from API:', data);

    return Array.isArray(data) ? data : []; // Ensure array return

  } catch (error) {
    logger.error('Error fetching agents:', error);
    if (error instanceof ApiError) throw error;
    throw new ApiError('Failed to fetch agents', 500, error.message);
  }
};

export const createAgent = async (tenantId, headers) => { // Expect headers object
    try {
        if (!tenantId) throw new ApiError('Tenant ID is required', 400);
        // Backend expects only tenant_id in path, no body needed for create
        const response = await fetch(`${API_URL}${TENANTS_ENDPOINT}/${tenantId}/agents`, {
            method: 'POST',
            headers: headers, // Use the passed headers directly
            // No body needed based on router definition
        });
        const data = await response.json();
        if (!response.ok) {
            throw new ApiError(data.detail || 'Failed to create agent', response.status);
        }
        return data; // Returns the created agent object
    } catch (error) {
        logger.error('Error creating agent:', error);
        if (error instanceof ApiError) throw error;
        throw new ApiError('Failed to create agent', 500, error.message);
    }
};

export const registerAgent = async (agentId, headers) => { // Expect headers object
     try {
         if (!agentId) throw new ApiError('Agent ID is required', 400);
         // Endpoint is /agents/{agent_id}/register
         const response = await fetch(`${API_URL}${AGENTS_ENDPOINT}/${agentId}/register`, {
             method: 'POST',
             headers: headers, // Use the passed headers directly
             // No body needed
         });
         const data = await response.json();
         if (!response.ok) {
             throw new ApiError(data.detail || 'Failed to register agent', response.status);
         }
        return data; // Returns registration status details for the agent
     } catch (error) {
         logger.error('Error registering agent:', error);
         if (error instanceof ApiError) throw error;
         throw new ApiError('Failed to register agent', 500, error.message);
     }
 };


export const deleteAgent = async (tenantId, agentId, headers) => { // Expect headers object
    logger.debug("API Client: Deleting Agent", { tenantId, agentId });
    // TODO: Implement actual API call to DELETE /tenants/{tenantId}/agents/{agentId}
     const endpoint = `${API_URL}${TENANTS_ENDPOINT}/${tenantId}/agents/${agentId}`;
     try {
         const response = await fetch(endpoint, {
             method: 'DELETE',
             headers: headers, // Use the passed headers directly
         });
         if (!response.ok && response.status !== 204) {
             let errorDetails = 'Failed to delete agent';
             try { errorDetails = (await response.json()).detail || errorDetails } catch(e){}
             throw new ApiError(errorDetails, response.status);
         }
         return true; // Indicate success
     } catch (error) {
          logger.error('Error deleting agent:', error);
          if (error instanceof ApiError) throw error;
          throw new ApiError('Failed to delete agent', 500, error.message);
     }
};

/**
 * Get local deployment requirements for an agent
 * @param {string} tenantId - Tenant identifier
 * @param {string} agentId - Agent identifier  
 * @param {Object} headers - Request headers
 * @returns {Promise<Object>} Deployment requirements
 */
export const getLocalDeploymentRequirements = async (tenantId, agentId, headers) => {
    logger.debug("API Client: Getting local deployment requirements", { tenantId, agentId });
    const endpoint = `${API_URL}${TENANTS_ENDPOINT}/${tenantId}/agents/${agentId}/deploy/local/requirements`;
    try {
        const response = await fetch(endpoint, {
            method: 'GET',
            headers: headers,
        });
        if (!response.ok) {
            let errorDetails = 'Failed to get deployment requirements';
            try { errorDetails = (await response.json()).detail || errorDetails } catch(e){}
            throw new ApiError(errorDetails, response.status);
        }
        return await response.json();
    } catch (error) {
        logger.error('Error getting deployment requirements:', error);
        if (error instanceof ApiError) throw error;
        throw new ApiError('Failed to get deployment requirements', 500, error.message);
    }
};

/**
 * Generate and download local deployment package
 * @param {string} tenantId - Tenant identifier
 * @param {string} agentId - Agent identifier
 * @param {Object} packageConfig - Package configuration
 * @param {Object} headers - Request headers
 * @returns {Promise<Blob>} Package ZIP file
 */
export const generateLocalPackage = async (tenantId, agentId, packageConfig, headers) => {
    logger.debug("API Client: Generating local package", { tenantId, agentId, packageConfig });
    const endpoint = `${API_URL}${TENANTS_ENDPOINT}/${tenantId}/agents/${agentId}/deploy/local/package`;
    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                ...headers,
                'Content-Type': 'application/json',
                'Accept': 'application/zip'
            },
            body: JSON.stringify(packageConfig)
        });
        if (!response.ok) {
            let errorDetails = 'Failed to generate package';
            try { errorDetails = (await response.json()).detail || errorDetails } catch(e){}
            throw new ApiError(errorDetails, response.status);
        }
        return await response.blob();
    } catch (error) {
        logger.error('Error generating package:', error);
        if (error instanceof ApiError) throw error;
        throw new ApiError('Failed to generate package', 500, error.message);
    }
};
